/**
 * Jobs. District rail across the top, one featured chain hero, and a dense table of the
 * chain's steps. The right rail answers "what am I doing right now and what does it pay".
 *
 * Every number on this screen comes from the server. The client renders the cost of a beat
 * before the tap, because a cost is deterministic and printed on the button; it never renders a
 * payout it computed. `chain.jobs[n].payout` is the MEDIAN take the server priced against this
 * player's level, this district's earnings tier and the Beat, and the actual take arrives in the
 * response.
 */

import { useEffect, useRef, useState } from 'react'
import type { DistrictId, JobChainState, JobOutcome, JobStep, MasteryTier } from '@ashport/shared'
import { cents, JOB_DROP_CHANCE, JOB_RUN_MAX, MASTERY_BONUS, MASTERY_THRESHOLD, xpEarned } from '@ashport/shared/formulas'
import { A } from '../art'
import { Icon, Res } from '../ui/Icon'
import { Art, Btn, Chip, Col, mainFrame, Meter, Panel, PanelHead, Rail, Row, Spacer, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { MasteryBar, ResultPlate, type PlateItem, type PlateRow } from '../ui/ResultPlate'
import { exact, useGame } from '../game/state'
import { useSession } from '../game/session'

/* ------------------------------------------------------------------- types */

type Chain = JobChainState
type Job = JobStep
type Diff = JobStep['diff']

const DIFF_C: Record<Diff, string> = {
  Easy: 'var(--dirty)',
  Medium: 'var(--gold)',
  Hard: 'var(--heat)',
  'Very Hard': 'var(--nerve)',
  Extreme: 'var(--arrest)',
}

const FILTERS: { id: 'all' | 'open' | 'locked'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Available' },
  { id: 'locked', label: 'Locked' },
]

const MASTERY_C: Record<MasteryTier, string> = {
  none: 'var(--ink-faint)',
  bronze: '#a9743c',
  silver: '#b9c2c8',
  gold: 'var(--gold)',
}

/**
 * What the Beat is doing to THIS chain right now. The relation is server state; the multipliers
 * it implies are in `shared/formulas.ts` and identical on both sides.
 */
const BEAT_CHIP: Record<Chain['beat'], { label: string; c: string } | null> = {
  hot: { label: 'ON THE BEAT  +40% PAY', c: 'var(--heat)' },
  cooling: { label: 'COOLING  +15% PAY', c: 'var(--gold)' },
  cold: null,
}

/* ---------------------------------------------------------------- fragments */

function HeroStat({ label, value, c, icon }: { label: string; value: string; c?: string; icon?: 'rep' | 'clock' }) {
  return (
    <div style={{ padding: '7px 16px 8px', minWidth: 92 }}>
      <div className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-dim)' }}>
        {label}
      </div>
      <Row gap={5} style={{ marginTop: 2 }}>
        {icon && <Icon n={icon} s={13} c={c ?? 'var(--gold)'} />}
        <span className="t-head" style={{ fontSize: 15, color: c ?? 'var(--ink-hi)', letterSpacing: '.07em' }}>
          {value}
        </span>
      </Row>
    </div>
  )
}

function Divider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
}

function Hero({ chain, stacked }: { chain: Chain; stacked: boolean }) {
  const beat = BEAT_CHIP[chain.beat]
  /*
   * STACKED, the hero is a band with the title on it and the pitch and the stats in normal flow
   * beneath, because a 44px title in a 58 percent overlay of a 377px-wide plate broke mid-word and
   * ran under the stats strip. The art is still the first thing on the screen.
   */
  if (stacked) {
    return (
      <div style={{ flex: 'none', margin: '9px 10px 0', border: '1px solid var(--line)', background: 'rgba(5,7,9,.6)' }}>
        <Art src={A(chain.heroId)} alt={`${chain.title} chain art`} focal="72% 45%" style={{ height: 150 }}>
          <div style={{ position: 'absolute', inset: 'auto 0 0 0', padding: '0 14px 12px' }}>
            <Row gap={8} style={{ flexWrap: 'wrap' }}>
              <Icon n="jobs" s={12} c="var(--brass-lit)" />
              <span className="t-label" style={{ fontSize: sz(9), color: 'var(--brass-lit)' }}>
                Featured Job Chain
              </span>
              {beat && <Chip c={beat.c}>{beat.label}</Chip>}
            </Row>
            <div
              className="t-display"
              style={{
                marginTop: 6,
                fontSize: 32,
                color: 'var(--heat)',
                letterSpacing: '.045em',
                textShadow: '0 3px 0 #000, 0 0 30px rgba(240,90,50,.45)',
                overflowWrap: 'anywhere',
              }}
            >
              {chain.title}
            </div>
          </div>
        </Art>
        <p style={{ margin: 0, padding: '10px 14px 0', fontSize: sz(12), lineHeight: 1.55, color: 'var(--ink-mid)' }}>
          {chain.pitch[0]}
          <br />
          {chain.pitch[1]}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            marginTop: 10,
            borderTop: '1px solid var(--line)',
          }}
        >
          <HeroStat label="Chain Length" value={`${chain.len} Jobs`} />
          <HeroStat label="Est. Time" value={chain.time} icon="clock" c="var(--ink-hi)" />
          <HeroStat label="Difficulty" value={chain.diff.toUpperCase()} c={DIFF_C[chain.diff]} />
          <HeroStat
            label="Mastered"
            value={`${chain.jobs.filter((j) => j.mastery === 'gold').length} of ${chain.len}`}
            icon="rep"
            c="var(--gold)"
          />
        </div>
      </div>
    )
  }
  return (
    <Art
      src={A(chain.heroId)}
      alt={`${chain.title} chain art`}
      focal="72% 45%"
      style={{ height: 234, flex: 'none', margin: '9px 10px 0', border: '1px solid var(--line)' }}
    >
      <div style={{ position: 'absolute', inset: '0 auto 0 0', width: '58%', padding: '20px 0 0 22px' }}>
        <Row gap={8}>
          <Icon n="jobs" s={12} c="var(--brass-lit)" />
          <span className="t-label" style={{ fontSize: sz(9), color: 'var(--brass-lit)' }}>
            Featured Job Chain
          </span>
          {beat && <Chip c={beat.c}>{beat.label}</Chip>}
        </Row>
        <div
          className="t-display"
          style={{
            marginTop: 12,
            fontSize: 44,
            color: 'var(--heat)',
            letterSpacing: '.045em',
            textShadow: '0 3px 0 #000, 0 0 30px rgba(240,90,50,.45)',
          }}
        >
          {chain.title}
        </div>
        <p
          style={{
            margin: '14px 0 0',
            maxWidth: 460,
            fontSize: 12,
            lineHeight: 1.55,
            color: 'var(--ink-mid)',
            textShadow: '0 1px 6px #000',
          }}
        >
          {chain.pitch[0]}
          <br />
          {chain.pitch[1]}
        </p>
      </div>
      <Row
        gap={0}
        style={{
          position: 'absolute',
          left: 22,
          bottom: 18,
          background: 'linear-gradient(180deg, rgba(12,16,19,.86), rgba(5,7,9,.92))',
          border: '1px solid var(--line)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <HeroStat label="Chain Length" value={`${chain.len} Jobs`} />
        <Divider />
        <HeroStat label="Est. Time" value={chain.time} icon="clock" c="var(--ink-hi)" />
        <Divider />
        <HeroStat label="Difficulty" value={chain.diff.toUpperCase()} c={DIFF_C[chain.diff]} />
        <Divider />
        {/* Beats at Gold, which is real. The old "Reputation +N" here was `goldCount * 90`, a
            number with no system behind it, and it went the same way as the rewards panel's. */}
        <HeroStat
          label="Mastered"
          value={`${chain.jobs.filter((j) => j.mastery === 'gold').length} of ${chain.len}`}
          icon="rep"
          c="var(--gold)"
        />
      </Row>
    </Art>
  )
}

function JobRow({
  j,
  chain,
  sel,
  onSel,
  energy,
  onRun,
  busy,
}: {
  j: Job
  chain: Chain
  sel: boolean
  onSel: () => void
  energy: number
  onRun: (times: number) => void
  busy: boolean
}) {
  const locked = j.state === 'locked'
  const nameC =
    j.state === 'active'
      ? 'var(--heat)'
      : locked
        ? 'var(--ink-dim)'
        : j.state === 'done'
          ? 'var(--ink-mid)'
          : 'var(--ink-hi)'
  return (
    <tr
      data-on={j.state === 'active' || undefined}
      onClick={onSel}
      style={{
        cursor: 'pointer',
        opacity: locked ? 0.72 : 1,
        ...(sel && j.state !== 'active'
          ? {
              background: 'linear-gradient(90deg, rgba(214,168,75,.13), transparent 70%)',
              boxShadow: 'inset 2px 0 0 var(--gold)',
            }
          : null),
      }}
    >
      <td>
        <Row gap={9}>
          <span style={{ width: 9, flex: 'none' }}>
            {j.state === 'active' && <Icon n="chev" s={12} c="var(--heat)" />}
          </span>
          <Art
            src={A(j.iconId)}
            alt={j.name}
            scrim="none"
            style={{
              width: 30,
              height: 30,
              flex: 'none',
              border: `1px solid ${j.state === 'active' ? 'var(--line-brass-hot)' : 'var(--line)'}`,
              filter: locked ? 'grayscale(.85) brightness(.7)' : undefined,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              className="t-head"
              style={{ fontSize: 11.5, color: nameC, letterSpacing: '.09em', whiteSpace: 'nowrap' }}
            >
              {j.name}
            </div>
            <div
              style={{
                fontSize: sz(10.5),
                color: 'var(--ink-dim)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {j.desc}
            </div>
            <BeatFacts j={j} compact />
          </div>
        </Row>
      </td>
      <td>
        <Row gap={6}>
          <Res k="dirty" s={13} />
          <span className="t-num" style={{ fontSize: 12.5, color: 'var(--dirty)' }}>
            {exact(j.payout)}
          </span>
        </Row>
      </td>
      <td>
        <Row gap={5}>
          <Res k="energy" s={12} />
          <span
            className="t-num"
            style={{ fontSize: 12.5, color: j.energy > energy ? 'var(--arrest)' : 'var(--ink-hi)' }}
          >
            {j.energy}
          </span>
        </Row>
      </td>
      <td>
        {/* Always zero, and it is load-bearing. Jobs are the floor a player works when Heat is
            high, which is what turns "wait for decay" into "switch activity". */}
        <Row gap={5}>
          <Res k="heat" s={12} />
          <span className="t-num" style={{ fontSize: 12.5, color: 'var(--dirty)' }}>
            None
          </span>
        </Row>
      </td>
      <td>
        <Chip c={DIFF_C[j.diff]}>{j.diff}</Chip>
      </td>
      <td>
        <span className="t-mono" style={{ fontSize: 11.5, color: 'var(--ink-mid)' }}>
          {j.step} / {chain.len}
        </span>
        {/* Mastery is per beat, not per chain. Step 4 can be Gold while step 5 is untouched,
            which is the whole difference between a chain and a lockstep tier gate. */}
        {/* Phase 20: the bar, with the three tiers marked on it, in place of a word and a count. */}
        <div style={{ marginTop: 4 }}>
          <MasteryBar runs={j.runs} tier={j.mastery} compact />
        </div>
      </td>
      <td style={{ textAlign: 'right' }}>
        <RunButton j={j} energy={energy} busy={busy} onRun={onRun} />
      </td>
    </tr>
  )
}

/** "12%" from 0.12, with no decimals the server did not roll. */
const pct = (x: number) => `${Math.round(x * 100)}%`

/** True when the beat needs something the pockets do not hold. The server refuses the same way. */
const short = (j: Job) => j.requires !== null && !j.requires.spent && j.requires.have < j.requires.qty

/**
 * PHASE 22. WHAT FALLS OUT, AND WHAT IT TAKES, on the row, in the row's own words.
 *
 * The odds are the server's number for THIS beat at THIS mastery, so the line moves when the
 * bar does: a beat at Bronze says 12%, the same beat at Gold says 22%. The requirement names the
 * thing, the count held, and the beat that drops it, which is the whole difference between a
 * gate and a padlock. Both lines are facts the server enforces, never decoration.
 */
function BeatFacts({ j, compact }: { j: Job; compact?: boolean }) {
  const d = j.drops
  const r = j.requires
  const pool = d.materials.join(', ') + (d.gear > 0 ? `${d.materials.length ? ' or ' : ''}tier ${d.tier} gear` : '')
  const missing = short(j)
  return (
    <div style={{ marginTop: compact ? 3 : 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Row gap={5} style={{ minWidth: 0 }}>
        <Icon n="crate" s={11} c="var(--gold)" />
        <span
          className="t-mono"
          style={{
            fontSize: sz(compact ? 10 : 10.5),
            color: 'var(--ink-mid)',
            whiteSpace: compact ? 'nowrap' : undefined,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
          title={`${pct(d.chance)} a run at ${j.mastery === 'none' ? 'no' : j.mastery} mastery, ${pct(JOB_DROP_CHANCE.gold)} at Gold: ${pool}`}
        >
          <span style={{ color: 'var(--gold)' }}>{pct(d.chance)}</span> drops {pool || 'nothing'}
          {j.mastery !== 'gold' ? `, ${pct(JOB_DROP_CHANCE.gold)} at Gold` : ''}
        </span>
      </Row>
      {r && (
        <Row gap={5} style={{ minWidth: 0 }}>
          <Icon n={r.spent ? 'check' : 'lock'} s={11} c={r.spent ? 'var(--dirty)' : missing ? 'var(--arrest)' : 'var(--dirty)'} />
          <span
            className="t-mono"
            style={{
              fontSize: sz(compact ? 10 : 10.5),
              color: r.spent ? 'var(--ink-faint)' : missing ? 'var(--arrest)' : 'var(--dirty)',
              whiteSpace: compact ? 'nowrap' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
            title={r.droppedBy ? `${r.droppedBy} drops it. The Crusher makes it from anything you can spare.` : 'The Crusher makes it from anything you can spare.'}
          >
            {r.spent
              ? `${r.name} spent to open it`
              : `Needs ${r.qty} ${r.name}, you have ${r.have}${r.droppedBy ? `. ${r.droppedBy} drops it` : ''}`}
          </span>
        </Row>
      )}
    </div>
  )
}

/** The one control on the row, shared by the table row and the stacked card. Two, since Phase 20. */
function RunButton({ j, energy, busy, onRun }: { j: Job; energy: number; busy: boolean; onRun: (times: number) => void }) {
  const locked = j.state === 'locked'
  const broke = j.energy > energy
  if (locked) {
    // The REQUIREMENT, not a padlock. "Locked" tells a player they are unwelcome;
    // "2 more runs on The Padlock" tells them what to do next, and the server counted it.
    return (
      <Btn size="sm" full icon="lock" disabled title={j.lockedReason ?? 'Locked'}>
        {j.lockedReason ?? 'Locked'}
      </Btn>
    )
  }
  if (j.state === 'done') {
    return (
      <Btn v="ghost" size="sm" full icon="check" disabled>
        Gold
      </Btn>
    )
  }
  if (short(j) && j.requires) {
    // PHASE 22. The requirement, on the button, the way the mastery gate is. The server would
    // refuse with the same sentence; the button says it first so the tap is never wasted.
    const r = j.requires
    return (
      <Btn
        size="sm"
        full
        icon="lock"
        disabled
        title={r.droppedBy ? `${r.droppedBy} drops it, and the Crusher makes it from anything you can spare.` : 'The Crusher makes it from anything you can spare.'}
      >
        {`Needs ${r.qty === 1 ? 'a' : r.qty} ${r.name}`}
      </Btn>
    )
  }
  const three = j.energy * JOB_RUN_MAX
  const brokeThree = three > energy
  return (
    <Row gap={5} style={{ minWidth: 0 }}>
      <Btn
        v={j.state === 'active' ? 'danger' : 'gold'}
        size="sm"
        disabled={busy || broke}
        title={broke ? `Wants ${j.energy} Energy` : `Run this beat for ${j.energy} Energy`}
        onClick={() => onRun(1)}
        style={{ flex: '1 1 0', minWidth: 0 }}
      >
        {busy ? 'Working' : broke ? `Needs ${j.energy}` : `Run  ${j.energy}E`}
      </Btn>
      {/*
        * RUN ×3, Phase 20. One tap, three runs, one plate: Bronze is three runs and the burst
        * session wants three taps to Bronze rather than nine. The server runs all three in one
        * write run and refuses when there is not Energy for all three, so the total on the button
        * is the total it charges.
        */}
      <Btn
        v="ghost"
        size="sm"
        disabled={busy || brokeThree}
        title={brokeThree ? `Wants ${three} Energy` : `Run this beat ${JOB_RUN_MAX} times for ${three} Energy`}
        onClick={() => onRun(JOB_RUN_MAX)}
        style={{ flex: 'none' }}
      >
        {`×${JOB_RUN_MAX}  ${three}E`}
      </Btn>
    </Row>
  )
}

/**
 * THE ROW AS A CARD, for a stacked screen. The table above fixes six column widths and needs
 * about 1,250px to show its Run button; under 1024px the same facts stack into a card with the
 * button along the bottom, where a thumb finds it. Same data, same server numbers, no table.
 */
function JobCard({
  j,
  chain,
  sel,
  onSel,
  energy,
  onRun,
  busy,
}: {
  j: Job
  chain: Chain
  sel: boolean
  onSel: () => void
  energy: number
  onRun: (times: number) => void
  busy: boolean
}) {
  const locked = j.state === 'locked'
  const active = j.state === 'active'
  const nameC = active ? 'var(--heat)' : locked ? 'var(--ink-dim)' : j.state === 'done' ? 'var(--ink-mid)' : 'var(--ink-hi)'
  return (
    <div
      onClick={onSel}
      style={{
        padding: '10px 10px 10px 12px',
        borderBottom: '1px solid var(--line-soft)',
        opacity: locked ? 0.72 : 1,
        background: active
          ? 'linear-gradient(90deg, rgba(240,90,50,.15), transparent 70%)'
          : sel
            ? 'linear-gradient(90deg, rgba(214,168,75,.13), transparent 70%)'
            : undefined,
        boxShadow: active ? 'inset 2px 0 0 var(--heat)' : sel ? 'inset 2px 0 0 var(--gold)' : undefined,
      }}
    >
      <Row gap={10} style={{ alignItems: 'flex-start' }}>
        <Art
          src={A(j.iconId)}
          alt={j.name}
          scrim="none"
          style={{
            width: 42,
            height: 42,
            flex: 'none',
            border: `1px solid ${active ? 'var(--line-brass-hot)' : 'var(--line)'}`,
            filter: locked ? 'grayscale(.85) brightness(.7)' : undefined,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-head" style={{ fontSize: sz(12.5), color: nameC, letterSpacing: '.08em' }}>
            {j.name}
          </div>
          <div style={{ fontSize: sz(11), color: 'var(--ink-dim)', lineHeight: 1.4 }}>{j.desc}</div>
          <Row gap={12} style={{ marginTop: 6, flexWrap: 'wrap', rowGap: 4 }}>
            <Row gap={5}>
              <Res k="dirty" s={13} />
              <span className="t-num" style={{ fontSize: sz(12.5), color: 'var(--dirty)' }}>
                {exact(j.payout)}
              </span>
            </Row>
            <Row gap={5}>
              <Res k="energy" s={12} />
              <span className="t-num" style={{ fontSize: sz(12.5), color: j.energy > energy ? 'var(--arrest)' : 'var(--ink-hi)' }}>
                {j.energy}
              </span>
            </Row>
            <Chip c={DIFF_C[j.diff]}>{j.diff}</Chip>
            <span className="t-mono" style={{ fontSize: sz(11), color: 'var(--ink-mid)' }}>
              {j.step} / {chain.len}
            </span>
          </Row>
          <div style={{ marginTop: 6 }}>
            <MasteryBar runs={j.runs} tier={j.mastery} />
          </div>
          <BeatFacts j={j} />
        </div>
      </Row>
      <div style={{ marginTop: 9 }}>
        <RunButton j={j} energy={energy} busy={busy} onRun={onRun} />
      </div>
    </div>
  )
}

function ObjectivePanel({ chain }: { chain: Chain }) {
  const act = chain.jobs.find((j) => j.id === chain.focus) ?? chain.jobs[0]
  const o = chain.objective
  const hit = o.checks.filter((c) => c.have >= c.need).length
  return (
    <Panel tone={o.done ? 'safe' : 'danger'}>
      <PanelHead
        title="Current Objective"
        icon={o.done ? 'check' : 'target'}
        accent={o.done ? 'var(--dirty)' : 'var(--heat)'}
      />
      <div style={{ padding: '9px 11px 11px' }}>
        <div className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-dim)' }}>
          {chain.title} &middot; {o.done ? `${chain.len} of ${chain.len} cleared` : `Step ${act.step} of ${chain.len}`}
        </div>
        <div className="t-head" style={{ fontSize: 15, color: 'var(--ink-hi)', marginTop: 4, letterSpacing: '.08em' }}>
          {o.title}
        </div>
        {/* Six of the twelve chains are DOSSIER format and carry a mission emblem rather than a
            scene, and an emblem stretched across a 58px strip is a black bar with a stamp in it.
            Fall back to the chain's own hero art in that case. */}
        <Art
          src={A(act.artId.startsWith('icons.') ? chain.heroId : act.artId, chain.heroId)}
          alt={`${o.title} scene`}
          scrim="soft"
          style={{ height: 58, marginTop: 8, border: '1px solid var(--line)' }}
        />
        <p style={{ margin: '9px 0 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--ink-mid)' }}>{o.body}</p>

        <Col gap={0} style={{ marginTop: 10 }}>
          {o.checks.map((c) => {
            const ok = c.have >= c.need
            return (
              <Row key={c.t} gap={8} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <span
                  style={{
                    width: 13,
                    height: 13,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    border: `1px solid ${ok ? 'var(--dirty)' : 'var(--line)'}`,
                    background: ok ? 'color-mix(in srgb, var(--dirty) 22%, transparent)' : '#05080a',
                  }}
                >
                  {ok && <Icon n="check" s={9} c="var(--dirty)" />}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 11,
                    color: ok ? 'var(--ink-dim)' : 'var(--ink)',
                    textDecoration: ok ? 'line-through' : undefined,
                  }}
                >
                  {c.t}
                </span>
                <span className="t-mono" style={{ fontSize: sz(10.5), color: ok ? 'var(--dirty)' : 'var(--ink-dim)' }}>
                  {c.have} / {c.need}
                </span>
              </Row>
            )
          })}
        </Col>

        <div style={{ marginTop: 9 }}>
          <Meter
            v={hit}
            max={o.checks.length}
            a={o.done ? 'var(--dirty)' : 'var(--brass-deep)'}
            b={o.done ? 'var(--dirty)' : 'var(--heat)'}
            h={4}
          />
        </div>
        <Btn v="ghost" size="sm" full icon="chev" style={{ marginTop: 9 }}>
          {o.done ? 'View Chain Summary' : 'View Job Details'}
        </Btn>
      </div>
    </Panel>
  )
}

/**
 * What the beat pays. The cash figure is the MEDIAN take the server priced for this player, not
 * a guess and not the top of the band. The real number arrives in the response.
 */
function RewardsPanel({ job, level }: { job: Job; level: number }) {
  /*
   * ONLY WHAT ACTUALLY PAYS OUT.
   *
   * Rep, Family Rep and Materials were on this panel as placeholders scaled off the take. They
   * are gone. A displayed reward with no system behind it teaches players that our numbers are
   * decorative, and in this game the numbers being real is the entire pitch: the bust threshold
   * on a crime tile IS the threshold the server rolls against, and that promise is worth nothing
   * if the panel next to it is making things up. They come back when they pay out.
   */
  const rows: { label: string; value: string; c: string; icon: 'dirty' | 'rep' | 'crate' }[] = [
    { label: 'Dirty Cash, typical', value: exact(job.payout), c: 'var(--dirty)', icon: 'dirty' },
    { label: 'Experience', value: `+${xpEarned(level, job.energy, false)}`, c: 'var(--gold)', icon: 'rep' },
    // PHASE 22. Materials are back on this panel because they pay out now: the odds are the
    // server's, the pool is the catalogue's, and both are on the row that rolls them.
    {
      label: `A drop, tier ${job.drops.tier}${job.mastery === 'gold' ? '' : ` (${pct(JOB_DROP_CHANCE.gold)} at Gold)`}`,
      value: pct(job.drops.chance),
      c: 'var(--gold)',
      icon: 'crate',
    },
  ]
  const bonus = cents(job.payout * MASTERY_BONUS[job.mastery] - job.payout)
  return (
    <Panel>
      <PanelHead title="Rewards" icon="crate" sub={job.name} />
      <div style={{ padding: '6px 11px 10px' }}>
        {rows.map((r) => (
          <Row key={r.label} gap={9} style={{ padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
            <Icon n={r.icon} s={13} c={r.c} fill={r.icon === 'dirty'} />
            <span style={{ flex: 1, fontSize: 11.5, color: 'var(--ink)' }}>{r.label}</span>
            <span className="t-num" style={{ fontSize: 12.5, color: r.c }}>
              {r.value}
            </span>
          </Row>
        ))}
        <Row gap={9} style={{ padding: '6px 0 0' }}>
          <Icon n="legend" s={13} c={MASTERY_C[job.mastery]} />
          <span style={{ flex: 1, fontSize: 11, color: 'var(--ink-dim)' }}>
            {job.mastery === 'none' ? 'No mastery yet' : `${job.mastery} mastery`}
          </span>
          <span className="t-num" style={{ fontSize: 12, color: MASTERY_C[job.mastery] }}>
            {bonus > 0 ? `+${exact(bonus)}` : 'included'}
          </span>
        </Row>
        <Row gap={9} style={{ padding: '2px 0 0' }}>
          <Icon n="heat" s={13} c="var(--dirty)" fill />
          <span style={{ flex: 1, fontSize: 11, color: 'var(--ink-dim)' }}>Heat on success</span>
          <span className="t-num" style={{ fontSize: 12, color: 'var(--dirty)' }}>
            None
          </span>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * The chain's mastery, which IS the streak. Runs on a beat raise it through Bronze at 3, Silver
 * at 9 and Gold at 19, and every tier raises that beat's payout for good.
 */
function MasteryPanel({ chain }: { chain: Chain }) {
  const done = chain.jobs.filter((j) => j.mastery !== 'none').length
  const gold = chain.jobs.filter((j) => j.mastery === 'gold').length
  return (
    <Panel tone="gold">
      <PanelHead title="Chain Mastery" icon="legend" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <div className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.1em' }}>
          {done} of {chain.len} beats opened
        </div>
        {/* Phase 20: a bar per beat with the tiers marked, in place of seven dots that never moved. */}
        <Col gap={6} style={{ marginTop: 9 }}>
          {chain.jobs.map((j) => (
            <div key={j.id}>
              <Row gap={6} style={{ marginBottom: 2 }}>
                <span
                  className="t-label"
                  style={{ fontSize: sz(8.5), color: j.state === 'locked' ? 'var(--ink-faint)' : 'var(--ink-mid)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {j.step}. {j.name}
                </span>
                <span className="t-label" style={{ fontSize: sz(8), color: MASTERY_C[j.mastery], letterSpacing: '.12em' }}>
                  {j.mastery === 'none' ? `${j.runs}/${MASTERY_THRESHOLD.bronze}` : j.mastery.toUpperCase()}
                </span>
              </Row>
              <MasteryBar runs={j.runs} tier={j.mastery} compact />
            </div>
          ))}
        </Col>
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          Three runs takes a beat to Bronze and opens the one after it. Nine is Silver, nineteen
          is Gold. Every tier pays more on that beat forever.
        </p>
        <Row gap={7} style={{ marginTop: 10 }}>
          <Row
            gap={6}
            style={{
              flex: 1,
              padding: '6px 8px',
              background: 'color-mix(in srgb, var(--dirty) 9%, transparent)',
              border: '1px solid color-mix(in srgb, var(--dirty) 30%, transparent)',
            }}
          >
            <Res k="dirty" s={13} />
            <span className="t-num" style={{ fontSize: 11.5, color: 'var(--dirty)' }}>
              Gold pays +{Math.round((MASTERY_BONUS.gold - 1) * 100)}%
            </span>
          </Row>
          <Row
            gap={6}
            style={{
              flex: 1,
              padding: '6px 8px',
              background: 'color-mix(in srgb, var(--gold) 9%, transparent)',
              border: '1px solid var(--line-brass)',
            }}
          >
            <Res k="rep" s={13} />
            <span className="t-num" style={{ fontSize: 11.5, color: 'var(--gold)' }}>
              {gold} at Gold
            </span>
          </Row>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * Twelve lines in Vincenzo's voice, one per district chain. This is UI copy rather than game
 * data, which is why it lives here and not in the content bundle.
 */
/*
 * `SPONSOR_NOTES` was here: twelve hand-written quotes, one per district chain, attributed by
 * name and portrait to Vincenzo "The Chair" Aiello, who is a canon character with an authored
 * voice and who says none of them.
 *
 * That is inventing canon in a screen, and it is the version of it that is hardest to spot,
 * because the portrait resolves, the name is spelled right and the prose is in voice. What
 * replaces it is the line the writer actually wrote: every chain in `content/jobs/*.json` carries
 * an `antagonist`, one sentence naming who is standing in the way of that chain and how they
 * behave, and until now nothing in the game rendered it.
 */
function ChainAntagonist({ chain }: { chain: Chain }) {
  if (!chain.antagonist) return null
  return (
    <Panel tone="gold">
      <PanelHead title="Who Is In The Way" icon="rank" accent="var(--gold)" />
      <Row gap={10} style={{ padding: '9px 11px 11px', alignItems: 'flex-start' }}>
        <Icon n="warn" s={20} c="var(--gold)" />
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: 'var(--ink)' }}>
          {chain.antagonist}
        </p>
      </Row>
    </Panel>
  )
}

/**
 * What the server decided. Every number here arrived in the response; none of it was computed
 * on this machine. That is the whole contract, and it is why the card can state the take to the
 * dollar rather than "about".
 */
function Outcome({ o, onClose }: { o: JobOutcome; onClose: () => void }) {
  const big = o.kind === 'big_score'
  /*
   * PHASE 22. THE DROPS, ON THE PLATE, the way the crime plate has drawn them since Phase 20:
   * ids off the wire, names and pictures off the catalogue the Market already holds.
   */
  const catalog = useSession((s) => s.catalog)
  const loadCatalog = useSession((s) => s.loadCatalog)
  const named = (o.drops?.length ?? 0) + (o.consumed ? 1 : 0)
  useEffect(() => {
    if (named > 0) void loadCatalog().catch(() => undefined)
  }, [named, loadCatalog])
  const name = (id: string) => catalog.get(id)?.name ?? id
  const items: PlateItem[] = (o.drops ?? []).map((d) => ({
    name: name(d.itemId),
    qty: d.qty,
    artId: catalog.get(d.itemId)?.artId ?? null,
    kind: 'drop' as const,
  }))
  const rows: PlateRow[] = [
    { icon: 'dirty', label: 'Dirty cash', value: o.dirtyGained, fmt: (n) => `+${exact(cents(n))}`, c: 'var(--dirty)', fill: true },
    { icon: 'rep', label: 'Experience', value: o.xpGained, fmt: (n) => `+${n}`, c: 'var(--gold)' },
  ]
  if (o.consumed) {
    rows.push({ icon: 'lock', label: `${name(o.consumed.itemId)}, to open it`, value: `spent ×${o.consumed.qty}`, c: 'var(--ink-mid)' })
  }
  return (
    <ResultPlate
      tone={big ? 'gold' : 'safe'}
      title={big ? 'That Went Well' : o.times > 1 ? `Done, ${o.times} times` : 'Done'}
      artId={o.artId}
      fallbackArtId="jobs.results.success"
      headline={o.headline}
      body={o.body}
      rows={rows}
      items={items}
      mastery={{ runs: o.runs, from: o.runs - o.times, before: o.masteryBefore, after: o.masteryAfter, stepName: o.headline }}
      level={{ before: o.levelBefore, after: o.levelAfter, energyMax: o.energyMaxAfter }}
      next={
        o.stepAdvancedTo !== null
          ? `Beat ${o.stepAdvancedTo} is open now.`
          : o.chainComplete
            ? 'Every beat in the chain is at Gold.'
            : null
      }
      onClose={onClose}
    />
  )
}

/** A refusal, in voice, on the screen that caused it. Never a status code. */
function Refusal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Panel tone="danger">
      <PanelHead
        title="No"
        icon="warn"
        accent="var(--arrest)"
        right={
          <button
            onClick={onClose}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <p style={{ margin: 0, padding: '9px 11px 11px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Jobs() {
  const [dist, setDist] = useState<DistrictId | null>(null)
  const [filter, setFilter] = useState<'all' | 'open' | 'locked'>('all')
  const [sel, setSel] = useState<string | null>(null)
  const { player } = useGame()
  const { jobs, loadJobs, runJob, busy, lastJob, refusal, clearOutcome, clearRefusal } = useSession()
  const { stacked, vp, width, touch } = useViewport()
  // The table fixes six column widths and needs about 900px of panel to show its Run button,
  // which a 1366 laptop does not have beside a 306px rail. Under 1500px the rows are cards.
  const cards = stacked || width < 1500

  // Stacked, the plate lands above the list the thumb just scrolled; the screen goes back to it
  // ONCE per outcome, never on the clock's re-renders.
  const plate = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (stacked && lastJob) plate.current?.scrollIntoView({ block: 'center' })
  }, [stacked, lastJob])

  // One read on mount. It carries the chain state AND the screen's action token, which is why
  // there is no separate "give me a token" request: that would turn a single-use nonce into a
  // two-request dance an attacker can interleave.
  useEffect(() => {
    void loadJobs()
  }, [loadJobs])

  const chains = jobs?.chains ?? []
  if (chains.length === 0) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--ink-faint)' }}>
        <span className="t-head" style={{ letterSpacing: '.24em', fontSize: 11 }}>
          READING THE BOARD
        </span>
      </div>
    )
  }

  // Default to whatever is Hot: the Beat is the reason to be on this screen at this hour.
  const chain =
    chains.find((c) => c.id === dist) ?? chains.find((c) => c.beat === 'hot') ?? chains[0]
  const shown = chain.jobs.filter((j) =>
    filter === 'all' ? true : filter === 'locked' ? j.state === 'locked' : j.state !== 'locked',
  )
  const selJob = chain.jobs.find((j) => j.id === sel) ?? chain.jobs.find((j) => j.id === chain.focus) ?? chain.jobs[0]
  const cleared = chain.jobs.filter((j) => j.state === 'done').length

  return (
    <Stack>
      {/* main column */}
      <Panel style={mainFrame(stacked)}>
        <Row gap={14} style={{ padding: '8px 12px 6px', flex: 'none', flexWrap: 'wrap', rowGap: 6 }}>
          <span className="t-display" style={{ fontSize: 30, color: 'var(--ink-hi)', letterSpacing: '.06em' }}>
            Jobs
          </span>
          {vp !== 'phone' && (
            <span style={{ fontSize: 11.5, color: 'var(--ink-dim)' }}>
              Run the chain. Build the rep. Take the city one block at a time.
            </span>
          )}
          <Spacer />
          <Row gap={6}>
            <Chip c="var(--ink-mid)">{chains.length} Chains</Chip>
            <Chip c="var(--dirty)">{cleared} at Gold Here</Chip>
            <Chip c="var(--gold)">{player.rank} Clearance</Chip>
          </Row>
        </Row>

        <div style={{ padding: '0 10px', flex: 'none' }}>
          <Tabs
            items={chains.map((c) => ({
              id: c.id,
              label: c.beat === 'hot' ? `${c.label} *` : c.label,
            }))}
            value={chain.id}
            onChange={(v) => {
              setDist(v)
              setSel(null)
            }}
          />
        </div>

        <Hero chain={chain} stacked={stacked} />

        {/* Stacked, what just happened goes ABOVE the list, where the thumb that caused it is. */}
        {stacked && refusal && (
          <div style={{ margin: '10px 10px 0', flex: 'none' }}>
            <Refusal text={refusal} onClose={clearRefusal} />
          </div>
        )}
        {stacked && lastJob && (
          <div style={{ margin: '10px 10px 0', flex: 'none' }} ref={plate}>
            <Outcome o={lastJob} onClose={clearOutcome} />
          </div>
        )}

        <Row
          gap={9}
          style={{
            flex: 'none',
            flexWrap: 'wrap',
            rowGap: 6,
            margin: '10px 10px 0',
            padding: '6px 10px',
            borderTop: '1px solid var(--line)',
            borderBottom: '1px solid var(--line-soft)',
            background: 'rgba(255,255,255,.015)',
          }}
        >
          <Icon n="jobs" s={12} c="var(--brass-lit)" />
          <span className="t-label" style={{ fontSize: sz(10), color: 'var(--ink)' }}>
            Available Jobs in {chain.label}
          </span>
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
            {shown.length} of {chain.jobs.length}
          </span>
          <Spacer />
          <Icon n="filter" s={11} c="var(--ink-dim)" />
          <Row gap={4}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className="t-head"
                style={{
                  padding: touch ? '0 12px' : '3px 9px',
                  minHeight: touch ? 44 : undefined,
                  fontSize: sz(9.5),
                  letterSpacing: '.13em',
                  cursor: 'pointer',
                  color: filter === f.id ? 'var(--gold)' : 'var(--ink-dim)',
                  background: filter === f.id ? 'rgba(214,168,75,.12)' : 'transparent',
                  border: `1px solid ${filter === f.id ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                }}
              >
                {f.label}
              </button>
            ))}
          </Row>
        </Row>

        <div className="scroll-y" style={{ flex: 1, margin: '0 10px 10px' }}>
          {cards ? (
            <div>
              {shown.map((j) => (
                <JobCard
                  key={j.id}
                  j={j}
                  chain={chain}
                  sel={selJob.id === j.id}
                  onSel={() => setSel(j.id)}
                  energy={player.energy}
                  busy={busy}
                  onRun={(times) => {
                    setSel(j.id)
                    void runJob(chain.id, j.step, times)
                  }}
                />
              ))}
            </div>
          ) : (
          <table className="grid-tbl">
            <colgroup>
              <col />
              <col style={{ width: 132 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 116 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 156 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Job</th>
                <th>Payout</th>
                <th>Energy</th>
                <th>Heat</th>
                <th>Difficulty</th>
                <th>Chain Step</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((j) => (
                <JobRow
                  key={j.id}
                  j={j}
                  chain={chain}
                  sel={selJob.id === j.id}
                  onSel={() => setSel(j.id)}
                  energy={player.energy}
                  busy={busy}
                  onRun={(times) => {
                    setSel(j.id)
                    void runJob(chain.id, j.step, times)
                  }}
                />
              ))}
            </tbody>
          </table>
          )}
          {shown.length === 0 && (
            <div style={{ padding: '26px 0', textAlign: 'center', color: 'var(--ink-faint)' }}>
              <span className="t-head" style={{ letterSpacing: '.24em', fontSize: 11 }}>
                Nothing locked in {chain.label}
              </span>
            </div>
          )}
        </div>
      </Panel>

      {/* right rail */}
      <Rail width={306}>
        {!stacked && refusal && <Refusal text={refusal} onClose={clearRefusal} />}
        {!stacked && lastJob && <Outcome o={lastJob} onClose={clearOutcome} />}
        <ObjectivePanel chain={chain} />
        <RewardsPanel job={selJob} level={player.level} />
        <MasteryPanel chain={chain} />
        <ChainAntagonist chain={chain} />
      </Rail>
    </Stack>
  )
}
